import React, {useState, useEffect, useMemo} from 'react';
import {
    Button,
    Table,
    Space,
    Input,
    Form,
    Typography,
    Modal,
    message,
    Popconfirm,
    Layout,
    Row,
    Col,
    Card,
    Dropdown, MenuProps
} from 'antd';
import {
    SearchOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    DownOutlined,
    PrinterOutlined
} from '@ant-design/icons';
import {certificationService, useAuth} from '../../services';
import {
    CertificationCreateItem, CertificationDeleteItem,
    CertificationSearchRequest,
    CertificationSearchResponseItem,
    CertificationUpdateItem
} from "../../model";
import {useLocation} from "react-router-dom";
import {getEditableRoles, getPrintableRoles} from "../../utils/rolePermissions";
import CertificationPdfViewer from "./components/CertificationPdfViewer";
import { getPageSizeOptions, resetPaginationByTotal } from 'utils/pageUtil';

const {Title} = Typography;
const {Content} = Layout;

const CertificationPage: React.FC = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [certifications, setCertifications] = useState<CertificationSearchResponseItem[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentCertification, setCurrentCertification] = useState<CertificationSearchResponseItem | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editForm] = Form.useForm();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const {hasEditPermission, hasPrintPermission} = useAuth();
    const location = useLocation();
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [currentReportType, setCurrentReportType] = useState<string>("");
    const [reportTitle, setReportTitle] = useState<string>("");
    const [pdfViewerVisible, setPdfViewerVisible] = useState<boolean>(false);
    const items = [
        // /api/report/certification/exportCertificationMembersPdf
        // /api/report/certification/exportCertificationMembersExcel
        {key: 'CertificationMembers', label: '認證會員基本資料列表', icon: (<PrinterOutlined/>)}
    ];

    // 處理範圍輸入框的變更，使起始值成為迄值的預設值
    const handleStartValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        form.setFieldsValue({
            certificationCodeStart: value,
            certificationCodeEnd: value
        });
    };

    // 搜尋認證
    const handleSearch = async (values: any) => {
        setLoading(true);
        try {
            // 構建搜尋請求 - 確保所有欄位為空時傳送空字串而非 undefined
            const searchRequest: CertificationSearchRequest = {
                certificationCodeStart: values.certificationCodeStart || '',
                certificationCodeEnd: values.certificationCodeEnd || '',
                certificationName: values.certificationName || ''
            };

            // 調用API搜尋認證
            const result = await certificationService.search(searchRequest);

            if (result) {
                setCertifications(result);
                setTotalRecords(result.length);
                // 重置到第一頁
                setPagination(resetPaginationByTotal(result.length));

                // 如果沒有返回數據，顯示通知
                if (result.length === 0) {
                    message.info('查無資料');
                }
            } else {
                // 如果返回的結果為null（可能是由於網絡錯誤或其他問題）
                setCertifications([]);
                setTotalRecords(0);
                message.error('查詢失敗，請稍後重試');
            }
        } catch (error) {
            console.error('搜尋認證失敗:', error);
            message.error('搜尋失敗，請稍後重試');
            setCertifications([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    // 處理分頁、篩選和排序變更
    const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
        const newPagination = {
            current: paginationConfig.current,
            pageSize: paginationConfig.pageSize,
        };

        setPagination(newPagination);
    };

    // 重置表單
    const handleReset = () => {
        form.resetFields();
    };

    // 打開新增認證對話框
    const showAddModal = () => {
        setCurrentCertification(null);
        editForm.resetFields();
        setIsModalVisible(true);
    };

    // 打開編輯認證對話框
    const showEditModal = (certification: CertificationSearchResponseItem) => {
        setCurrentCertification(certification);
        editForm.setFieldsValue({
            certificationCode: certification.certificationCode,
            certificationName: certification.certificationName,
        });
        setIsModalVisible(true);
    };

    // 關閉對話框
    const handleCancel = () => {
        setIsModalVisible(false);
    };

    // 保存認證（新增或編輯）
    const handleSave = async () => {
        try {
            const values = await editForm.validateFields();
            setLoading(true);

            if (currentCertification) {
                // 編輯現有認證
                const updateItem: CertificationUpdateItem = {
                    certificationId: currentCertification.certificationId,
                    certificationCode: values.certificationCode,
                    certificationName: values.certificationName,
                    modifiedBy: 'currentUser' // 應從用戶會話中獲取
                };

                const updateResult = await certificationService.updateCertifications({
                    certifications: [updateItem]
                });

                if (updateResult && updateResult.updatedCount > 0) {
                    message.success('認證更新成功');
                    setIsModalVisible(false);
                    // 刷新認證列表
                    handleSearch(form.getFieldsValue());
                } else {
                    message.error('認證更新失敗');
                }
            } else {
                // 創建新認證
                const createItem: CertificationCreateItem = {
                    certificationCode: values.certificationCode,
                    certificationName: values.certificationName,
                    createdBy: 'currentUser' // 應從用戶會話中獲取
                };

                const createResult = await certificationService.createCertifications({
                    certifications: [createItem]
                });

                if (createResult && createResult.createdCount > 0) {
                    message.success('新增認證成功');
                    setIsModalVisible(false);
                    // 刷新認證列表
                    handleSearch(form.getFieldsValue());
                } else {
                    message.error('新增認證失敗');
                }
            }
        } catch (error) {
            console.error('保存認證失敗:', error);
            message.error('操作失敗，請稍後重試');
        } finally {
            setLoading(false);
        }
    };

    // 刪除認證
    const handleDelete = async (certificationId: number) => {
        setLoading(true);
        try {
            const deleteItem: CertificationDeleteItem = {certificationId};
            const deleteResult = await certificationService.deleteCertifications({
                certificationIds: [deleteItem]
            });

            if (deleteResult && deleteResult.deletedCount > 0) {
                message.success('認證刪除成功');
                // 刷新認證列表
                handleSearch(form.getFieldsValue());
            } else {
                message.error('認證刪除失敗');
            }
        } catch (error) {
            console.error('刪除認證失敗:', error);
            message.error('刪除失敗，請稍後重試');
        } finally {
            setLoading(false);
        }
    };

    // 處理預覽報表
    const handlePreviewReport = (reportType: string, title: string) => {
        // 檢查是否有選擇會員
        if (selectedRowKeys.length === 0) {
            message.warning('請至少選擇一個會員');
            return;
        }

        // 設置報表類型和標題
        setCurrentReportType(reportType);
        setReportTitle(title);

        // 顯示PDF預覽器
        setPdfViewerVisible(true);
    };

    // 關閉PDF預覽器
    const handleClosePdfViewer = () => {
        console.log('handleClosePdfViewer')
        setPdfViewerVisible(false);
    };

    const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
        const selectedItem = items.find(item => item.key === key);
        if (!selectedItem) return ;

        handlePreviewReport(key, selectedItem.label);
    };

    // 初始加載
    useEffect(() => {
        // 初始搜尋所有認證
        // handleSearch({});
    }, []);

    // 表格列定義
    const columns = [
        {
            title: '項次',
            dataIndex: 'index',
            key: 'index',
            render: (_: any, __: any, index: number) =>
                (pagination.current - 1) * pagination.pageSize + index + 1
        },
        {
            title: '認證代號',
            dataIndex: 'certificationCode',
            key: 'certificationCode'
        },
        {
            title: '認證名稱',
            dataIndex: 'certificationName',
            key: 'certificationName'
        },
        ...hasEditPermission(getEditableRoles(location.pathname)) ? [
            {
                title: '操作',
                key: 'action',
                render: (_: any, record: CertificationSearchResponseItem) => (
                    <Space size="middle">
                        <Button
                            icon={<EditOutlined/>}
                            onClick={() => showEditModal(record)}
                            title="編輯"
                        />
                        <Popconfirm
                            title="確定要刪除此認證嗎？"
                            okText="確定"
                            cancelText="取消"
                            onConfirm={() => handleDelete(record.certificationId)}
                        >
                            <Button
                                danger
                                icon={<DeleteOutlined/>}
                                title="刪除"
                            />
                        </Popconfirm>
                    </Space>
                )
            }
        ] : []
    ];

    const apiParam = useMemo(() => {
        return {
            certificationIds: selectedRowKeys.map(key => ({certificationId: key.toString()})),
        };
    }, [selectedRowKeys]);

    return (
        <Layout className="membership-page">
            <Content>
                {/* 頁面標題 */}
                <Row justify="center" className="mb-lg">
                    <Col>
                        <Title level={3} className="page-title">
                            <span className="page-title-icon">📜</span> 認證主檔
                        </Title>
                    </Col>
                </Row>

                {/* 搜尋表單 */}
                <Card
                    title={
                        <Space>
                            <SearchOutlined/>
                            <span>搜尋條件</span>
                        </Space>
                    }
                    className="card-content-spacing"
                >
                    <Form
                        form={form}
                        name="certificationSearch"
                        onFinish={handleSearch}
                        layout="horizontal"
                        labelCol={{xs: {span: 8}, md: {span: 6}}}
                        wrapperCol={{xs: {span: 16}, md: {span: 18}}}
                    >
                        <Row gutter={[24, 0]}>
                            {/* 認證代號範圍 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="認證代號">
                                    <Space style={{width: '100%'}}>
                                        <Form.Item
                                            name="certificationCodeStart"
                                            noStyle
                                            rules={[{pattern: /^\d*$/, message: '認證代號僅能輸入數字'}]}
                                        >
                                            <Input placeholder="起始值" style={{width: '100%'}}
                                                   onChange={handleStartValueChange}/>
                                        </Form.Item>
                                        <span>～</span>
                                        <Form.Item
                                            name="certificationCodeEnd"
                                            noStyle
                                            rules={[{pattern: /^\d*$/, message: '認證代號僅能輸入數字'}]}
                                        >
                                            <Input placeholder="結束值" style={{width: '100%'}}/>
                                        </Form.Item>
                                    </Space>
                                </Form.Item>
                            </Col>

                            {/* 認證名稱 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="認證名稱" name="certificationName">
                                    <Input placeholder="請輸入認證名稱"/>
                                </Form.Item>
                            </Col>

                            {/* 按鈕區域 */}
                            <Col xs={24}>
                                <Form.Item wrapperCol={{span: 24}}>
                                    <div className="form-button-container">
                                        <Button
                                            onClick={handleReset}
                                            icon={<DeleteOutlined/>}
                                        >
                                            清除
                                        </Button>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            icon={<SearchOutlined/>}
                                        >
                                            查詢
                                        </Button>
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>

                {/* 搜尋結果區域 */}
                <Card
                    title={
                        <>
                            <Content>
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        <Title level={5}>搜尋結果: {totalRecords} 筆記錄</Title>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <Button
                                                type="primary"
                                                icon={<PlusOutlined/>}
                                                onClick={showAddModal}
                                                disabled={!hasEditPermission(getEditableRoles(location.pathname))}
                                            >
                                                新增認證
                                            </Button>
                                            <Dropdown
                                                menu={{
                                                    items,
                                                    onClick: handleMenuClick,
                                                }}
                                                disabled={selectedRowKeys.length === 0}
                                                trigger={['click']}
                                            >
                                                <Button icon={<DownOutlined/>} disabled={selectedRowKeys.length === 0 || !hasPrintPermission(getPrintableRoles(location.pathname))}>
                                                    列印選項
                                                </Button>
                                            </Dropdown>
                                        </Space>
                                    </Col>
                                </Row>
                            </Content>
                        </>
                    }
                >
                    <Table
                        virtual
                        scroll={{ y: 600 }}
                        rowKey="certificationId"
                        dataSource={certifications}
                        columns={columns}
                        loading={loading}
                        pagination={{
                            ...pagination,
                            total: totalRecords,
                            showSizeChanger: true,
                            pageSizeOptions: getPageSizeOptions(totalRecords),
                            showTotal: (total) => `共 ${total} 筆`
                        }}
                        onChange={handleTableChange}
                        rowSelection={{
                            type: 'checkbox',
                            selectedRowKeys,
                            onChange: (selectedKeys) => {
                                setSelectedRowKeys(selectedKeys);
                            }
                        }}
                    />
                </Card>

                {/* 新增/編輯認證對話框 */}
                <Modal
                    title={currentCertification ? '編輯認證' : '新增認證'}
                    open={isModalVisible}
                    onOk={handleSave}
                    onCancel={handleCancel}
                    confirmLoading={loading}
                    okText="保存"
                    cancelText="取消"
                >
                    <Form
                        form={editForm}
                        layout="vertical"
                        name="certificationForm"
                    >
                        <Form.Item
                            name="certificationCode"
                            label="認證代號"
                            rules={[
                                {required: true, message: '請輸入認證代號'},
                                {pattern: /^\d+$/, message: '認證代號僅能輸入數字'}
                            ]}
                        >
                            <Input placeholder="請輸入認證代號"/>
                        </Form.Item>
                        <Form.Item
                            name="certificationName"
                            label="認證名稱"
                            rules={[{required: true, message: '請輸入認證名稱'}]}
                        >
                            <Input placeholder="請輸入認證名稱"/>
                        </Form.Item>
                    </Form>
                </Modal>
            </Content>

            {/* PDF預覽器組件 */}
            {
                pdfViewerVisible && (
                    <CertificationPdfViewer
                        visible={pdfViewerVisible}
                        onClose={handleClosePdfViewer}
                        reportType={currentReportType}
                        apiParam={apiParam}
                        title={reportTitle}
                    />
                )
            }
        </Layout>
    );
};

export default CertificationPage;
